import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ArrowLeft, Upload, Wine, LogOut, Trash2, Download, CheckSquare, Square } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import PullToRefresh from '../components/PullToRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function GalleryPage({ currentMember, onLogout }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/photos`);
      setPhotos(response.data);
      return true;
    } catch (error) {
      console.error('Error fetching photos:', error);
      toast.error('Failed to load photos');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    // Upload multiple files
    for (let file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        failCount++;
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploaded_by', currentMember);

      try {
        await axios.post(`${API}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        successCount++;
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} photo(s) uploaded!`);
      fetchPhotos();
    }
    if (failCount > 0) {
      toast.error(`${failCount} photo(s) failed to upload`);
    }

    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const togglePhotoSelection = (photoId) => {
    const newSelected = new Set(selectedPhotos);
    if (newSelected.has(photoId)) {
      newSelected.delete(photoId);
    } else {
      newSelected.add(photoId);
    }
    setSelectedPhotos(newSelected);
  };

  const selectAll = () => {
    setSelectedPhotos(new Set(photos.map(p => p.id)));
  };

  const clearSelection = () => {
    setSelectedPhotos(new Set());
    setSelectionMode(false);
  };

  const handleBulkDelete = async () => {
    if (selectedPhotos.size === 0) return;

    if (!window.confirm(`Delete ${selectedPhotos.size} photo(s)?`)) {
      return;
    }

    try {
      const photoIds = Array.from(selectedPhotos);
      await axios.post(`${API}/photos/bulk-delete`, photoIds, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      toast.success(`${photoIds.length} photo(s) deleted!`);
      setSelectedPhotos(new Set());
      setSelectionMode(false);
      fetchPhotos();
    } catch (error) {
      console.error('Error deleting photos:', error);
      toast.error('Failed to delete photos');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050203] flex items-center justify-center">
        <Wine className="w-12 h-12 text-[#E11D48] animate-pulse" />
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={fetchPhotos}>
      <div className="min-h-screen bg-[#050203] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'url(https://www.transparenttextures.com/patterns/black-felt.png)',
        }}
      />

      <div className="max-w-md mx-auto min-h-screen relative shadow-2xl border-x border-white/5">
        {/* Header */}
        <div className="bg-zinc-900/50 backdrop-blur-sm border-b border-white/10 p-6 sticky top-0 z-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-zinc-400 hover:text-[#E11D48] transition-colors">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="font-['Anton'] text-2xl text-white uppercase tracking-tight">
                  GALLERY
                </h1>
                <p className="text-zinc-500 text-xs uppercase tracking-widest">
                  {photos.length} photo{photos.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {photos.length > 0 && (
                <button
                  onClick={() => {
                    setSelectionMode(!selectionMode);
                    if (selectionMode) clearSelection();
                  }}
                  className="p-2 text-zinc-400 hover:text-[#E11D48] transition-colors"
                  title={selectionMode ? "Cancel" : "Select photos"}
                >
                  {selectionMode ? <Square className="w-6 h-6" /> : <CheckSquare className="w-6 h-6" />}
                </button>
              )}
              <button
                onClick={onLogout}
                data-testid="logout-button"
                className="p-2 text-zinc-400 hover:text-[#E11D48] transition-colors"
              >
                <LogOut className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Selection Actions Bar */}
          {selectionMode && (
            <div className="mt-4 flex items-center justify-between bg-zinc-800 p-3 rounded">
              <div className="text-white text-sm">
                {selectedPhotos.size} selected
              </div>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-xs text-zinc-400 hover:text-white uppercase tracking-wider"
                >
                  Select All
                </button>
                {selectedPhotos.size > 0 && (
                  <>
                    <span className="text-zinc-600">|</span>
                    <button
                      onClick={handleBulkDelete}
                      className="text-xs text-red-400 hover:text-red-300 uppercase tracking-wider flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Upload Section */}
        <div className="p-6">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="photo-upload"
          />
          <label htmlFor="photo-upload">
            <div
              data-testid="upload-button"
              className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white font-['Anton'] text-xl uppercase tracking-widest py-4 transition-colors border-2 border-transparent hover:border-white/20 flex items-center justify-center gap-3 cursor-pointer"
            >
              {uploading ? (
                <span>UPLOADING...</span>
              ) : (
                <>
                  <Upload className="w-6 h-6" />
                  <span>UPLOAD PHOTOS</span>
                </>
              )}
            </div>
          </label>
          <p className="text-zinc-600 text-xs text-center mt-2">
            You can select multiple photos at once
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="p-6 pt-0">
          {photos.length > 0 ? (
            <div data-testid="photo-grid" className="grid grid-cols-2 gap-2 md:gap-4">
              {photos.map((photo, index) => (
                <div
                  key={photo.id}
                  className={`relative bg-zinc-800 overflow-hidden group ${
                    index % 5 === 0 ? 'col-span-2 aspect-video' : 'aspect-square'
                  }`}
                  onClick={() => selectionMode && togglePhotoSelection(photo.id)}
                >
                  <img
                    src={`${BACKEND_URL}/api/uploads/${photo.filename}`}
                    alt="Long Island memory"
                    className={`w-full h-full object-cover transition-all duration-500 ${
                      selectionMode
                        ? 'cursor-pointer'
                        : 'grayscale group-hover:grayscale-0 group-hover:scale-105'
                    }`}
                    onError={(e) => {
                      console.error('Image load error:', photo.filename);
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23333"/><text x="50%" y="50%" text-anchor="middle" fill="%23999" font-size="12">Error</text></svg>';
                    }}
                  />
                  
                  {/* Selection Checkbox */}
                  {selectionMode && (
                    <div className="absolute top-2 right-2">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                        selectedPhotos.has(photo.id)
                          ? 'bg-[#E11D48] border-[#E11D48]'
                          : 'bg-black/50 border-white/50'
                      }`}>
                        {selectedPhotos.has(photo.id) && (
                          <CheckSquare className="w-4 h-4 text-white" />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Hover Info */}
                  {!selectionMode && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-2 left-2">
                        <p className="text-white text-xs font-bold uppercase tracking-wider">
                          {photo.uploaded_by}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Wine className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 text-xl uppercase tracking-wider">
                No photos yet
              </p>
              <p className="text-zinc-600 text-sm mt-2">Upload the first one!</p>
            </div>
          )}
        </div>
      </div>
      </div>
    </PullToRefresh>
  );
}

export default GalleryPage;
